/**
 * Current Presentation Search Tool
 *
 * Dedicated semantic search for the anchored current presentation.
 */

import { ToolBase } from "../../../base/toolBase";
import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { backendClient } from "../../../../../services/communication/backendClient";
import { createCompositeId } from "../../../../core/compositeIdUtils";
import { getContextResolver } from "../../../../core/contextResolver";
import GlobalStateManager from "../../../../globals/global-state/globalStateManager";
import {
    CurrentPresentationSearchParamsSchema,
    type CurrentPresentationSearchParams,
    type CurrentPresentationSearchResponse,
    type CurrentPresentationSearchResult,
} from "./currentPresentationSearch.schema";

const CURRENT_PRESENTATION_SEARCH_DESCRIPTION = `Search semantic concepts within the currently active presentation only.

Use this when you need to find candidate slides to inspect or update in the current deck.

Supports:
- One or more search phrases
- Query operator: "or" or "and"
- Result limit: 0 to 20 slides
- Optional deduplication using excludeAllPrevious
`;

const CURRENT_PRESENTATION_SEARCH_SYSTEM_PROMPT = `### currentPresentationSearch
Search semantic concepts in the current presentation only.

Use this tool to find slides in the active deck when the user is updating current work.

Inputs:
- phrases: one or more semantic search phrases
- queryOperator: "or" or "and"
- numResults: 0..20
- excludeAllPrevious: true/false

Example:
\`\`\`json
{
  "uiDescription": "Finding slides about operating model",
  "phrases": ["operating model", "target org structure"],
  "queryOperator": "or",
  "numResults": 8,
  "excludeAllPrevious": true
}
\`\`\``;

interface StoredCurrentPresentationSearchResult {
    id: string;
    resultType: "slide";
    resultId: string;
    presentationId: string;
    slideId: string;
    slideIndex?: number;
    score: number;
    similarity?: number;
    description?: string;
    text?: string;
    createdAt: string;
}

export class CurrentPresentationSearchTool extends ToolBase<
    CurrentPresentationSearchParams,
    CurrentPresentationSearchResponse
> {
    public readonly name = "currentPresentationSearch";
    public readonly description: ToolDescription;
    public readonly systemPromptDescription = CURRENT_PRESENTATION_SEARCH_SYSTEM_PROMPT;

    private currentdocumentId?: string;

    fieldsToCheck = [
        { name: "uiDescription", type: "string" as const, list_status: false, order: 0 },
        { name: "queryOperator", type: "string" as const, list_status: false, order: 2 },
        { name: "numResults", type: "number" as const, list_status: false, order: 3 },
        { name: "excludeAllPrevious", type: "boolean" as const, list_status: false, order: 4 },
    ];

    listsToCheck = [
        { name: "phrases", type: "array" as const, item_type: "string" as const, list_status: true, order: 1 },
    ];

    constructor() {
        super();
        this.description = new ToolDescription(
            this.name,
            CURRENT_PRESENTATION_SEARCH_DESCRIPTION,
            CurrentPresentationSearchParamsSchema
        );
    }

    public setCurrentdocumentId(documentId?: string): void {
        this.currentdocumentId = documentId;
    }

    public getCurrentdocumentId(): string | undefined {
        return this.currentdocumentId;
    }

    public async execute(parameters: unknown): Promise<CurrentPresentationSearchResponse> {
        const parseResult = safeParseToolParams(CurrentPresentationSearchParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(parseResult.error);
        }

        const params = parseResult.data;
        const documentId = this.currentdocumentId;
        if (!documentId) {
            throw new Error("Current presentation is not available for currentPresentationSearch.");
        }

        const phrases = [...new Set(params.phrases.map((p) => p.trim()).filter(Boolean))];
        if (phrases.length === 0 || params.numResults === 0) {
            return {
                results: [],
                count: 0,
                documentId,
                phrases,
                queryOperator: params.queryOperator,
                numResults: params.numResults,
            };
        }

        console.log(
            `[CurrentPresentationSearchTool] Searching current deck ${documentId} with ${phrases.length} phrase(s), ` +
            `operator=${params.queryOperator}, numResults=${params.numResults}, excludeAllPrevious=${params.excludeAllPrevious}`
        );

        const backendResponse = await backendClient.searchUnified({
            documentId,
            searchType: "semantic",
            resultTypes: ["slide"],
            queries: phrases,
            queryOperator: params.queryOperator,
            numResults: params.numResults,
            slidesToExclude: params.slidesToExclude && params.slidesToExclude.length > 0 ? params.slidesToExclude : undefined,
            enrichResults: true,
        });

        const contextResolver = getContextResolver();
        const mapped: CurrentPresentationSearchResult[] = [];
        const imageIds: string[] = [];

        for (const raw of (backendResponse.results || []) as Array<Record<string, unknown>>) {
            const presentationId = String(raw.presentation_id || documentId);
            const resultId = String(raw.result_id || raw.slide_id || "");
            const slideId = String(raw.slide_id || resultId);
            if (!resultId || !slideId) {
                continue;
            }

            const contextId = createCompositeId(presentationId, resultId);
            const result: CurrentPresentationSearchResult = {
                contextId,
                presentationId,
                slideId,
                resultId,
                slideIndex: typeof raw.slide_index === "number" ? raw.slide_index : undefined,
                score: typeof raw.score === "number" ? raw.score : 0,
                similarity: typeof raw.similarity === "number" ? raw.similarity : undefined,
                description: typeof raw.description === "string" ? raw.description : undefined,
                text: typeof raw.text === "string" ? raw.text : undefined,
            };
            mapped.push(result);

            const stored: StoredCurrentPresentationSearchResult = {
                id: contextId,
                resultType: "slide",
                resultId,
                presentationId,
                slideId,
                slideIndex: result.slideIndex,
                score: result.score,
                similarity: result.similarity,
                description: result.description,
                text: result.text,
                createdAt: new Date().toISOString(),
            };
            GlobalStateManager.getInstance().addOrUpdateContext(contextId, stored);

            const imageBase64 = typeof raw.image_base64 === "string" ? raw.image_base64 : undefined;
            if (imageBase64) {
                await contextResolver.storeImageAsync(contextId, imageBase64, "image/png", {
                    resultType: "slide",
                    presentationId,
                    slideId,
                    score: result.score,
                });
                imageIds.push(contextId);
            }
        }

        console.log(`[CurrentPresentationSearchTool] Found ${mapped.length} slide result(s)`);

        return {
            results: mapped,
            count: mapped.length,
            documentId,
            phrases,
            queryOperator: params.queryOperator,
            numResults: params.numResults,
            searchTimeMs: backendResponse.search_time_ms as number | undefined,
            _imageIds: imageIds.length > 0 ? imageIds : undefined,
        };
    }
}

export default CurrentPresentationSearchTool;

